import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
	AcceptMontyActionDocument,
	AcceptMontyActionMutation,
	CancelMontyLearnRunDocument,
	MontyInstructionsDocument,
	MontyInstructionsQuery,
	MontyLearnEstimateDocument,
	MontyLearnEstimateQuery,
	MontyLearnRunDocument,
	MontyLearnRunQuery,
	MontyProposalStatus,
	MontyStatusDocument,
	MontyStatusQuery,
	MontyTag,
	MontyUsageDocument,
	MontyUsageQuery,
	RemoveMontyApiKeyDocument,
	ResolveMontyProposalDocument,
	ResumeMontyLearnRunDocument,
	ResumeMontyLearnRunMutation,
	SaveMontyApiKeyDocument,
	SaveMontyApiKeyMutation,
	SaveMontyInstructionsDocument,
	SaveMontyInstructionsMutation,
	StartMontyLearnRunDocument,
	StartMontyLearnRunMutation,
} from '../../graphql/generated/gen-types';

// Monty v2 (088): settings + proposal resolution. Tags and proposals
// themselves stream over the existing Firestore message listeners.
@Injectable({ providedIn: 'root' })
export class MontyService {
	constructor(private apollo: Apollo) {}

	getStatus(): Observable<MontyStatusQuery['montyStatus']> {
		return this.apollo
			.query<MontyStatusQuery>({ query: MontyStatusDocument, fetchPolicy: 'network-only' })
			.pipe(map((result) => result.data.montyStatus));
	}

	getUsage(months = 3): Observable<MontyUsageQuery['montyUsage']> {
		return this.apollo
			.query<MontyUsageQuery>({ query: MontyUsageDocument, variables: { months }, fetchPolicy: 'network-only' })
			.pipe(map((result) => result.data.montyUsage));
	}

	saveApiKey(key: string): Observable<SaveMontyApiKeyMutation['saveMontyApiKey']> {
		return this.apollo
			.mutate<SaveMontyApiKeyMutation>({ mutation: SaveMontyApiKeyDocument, variables: { key } })
			.pipe(map((result) => result.data.saveMontyApiKey));
	}

	removeApiKey(): Observable<SaveMontyApiKeyMutation['saveMontyApiKey']> {
		return this.apollo.mutate<any>({ mutation: RemoveMontyApiKeyDocument }).pipe(map((result) => result.data.removeMontyApiKey));
	}

	acceptAction(messagePath: string, flightId?: string): Observable<AcceptMontyActionMutation['acceptMontyAction']> {
		return this.apollo
			.mutate<AcceptMontyActionMutation>({ mutation: AcceptMontyActionDocument, variables: { messagePath, flightId } })
			.pipe(map((result) => result.data.acceptMontyAction));
	}

	resolveProposal(messagePath: string, resolution: MontyProposalStatus): Observable<boolean> {
		return this.apollo
			.mutate<any>({ mutation: ResolveMontyProposalDocument, variables: { messagePath, resolution } })
			.pipe(map((result) => result.data.resolveMontyProposal));
	}

	// ── phase 2 (091): instructions memory & learn from history ──

	getInstructions(): Observable<MontyInstructionsQuery['montyInstructions']> {
		return this.apollo
			.query<MontyInstructionsQuery>({ query: MontyInstructionsDocument, fetchPolicy: 'network-only' })
			.pipe(map((result) => result.data.montyInstructions));
	}

	saveInstructions(
		tag: MontyTag,
		manual: string,
		learnedDraft?: string | null,
	): Observable<SaveMontyInstructionsMutation['saveMontyInstructions']> {
		return this.apollo
			.mutate<SaveMontyInstructionsMutation>({ mutation: SaveMontyInstructionsDocument, variables: { tag, manual, learnedDraft } })
			.pipe(map((result) => result.data.saveMontyInstructions));
	}

	getLearnEstimates(): Observable<MontyLearnEstimateQuery['montyLearnEstimate']> {
		return this.apollo
			.query<MontyLearnEstimateQuery>({ query: MontyLearnEstimateDocument, fetchPolicy: 'network-only' })
			.pipe(map((result) => result.data.montyLearnEstimate));
	}

	getLearnRun(): Observable<MontyLearnRunQuery['montyLearnRun']> {
		return this.apollo
			.query<MontyLearnRunQuery>({ query: MontyLearnRunDocument, fetchPolicy: 'network-only' })
			.pipe(map((result) => result.data.montyLearnRun));
	}

	startLearnRun(months: number): Observable<StartMontyLearnRunMutation['startMontyLearnRun']> {
		return this.apollo
			.mutate<StartMontyLearnRunMutation>({ mutation: StartMontyLearnRunDocument, variables: { months } })
			.pipe(map((result) => result.data.startMontyLearnRun));
	}

	cancelLearnRun(runId: string): Observable<boolean> {
		return this.apollo
			.mutate<any>({ mutation: CancelMontyLearnRunDocument, variables: { runId } })
			.pipe(map((result) => result.data.cancelMontyLearnRun));
	}

	resumeLearnRun(runId: string): Observable<ResumeMontyLearnRunMutation['resumeMontyLearnRun']> {
		return this.apollo
			.mutate<ResumeMontyLearnRunMutation>({ mutation: ResumeMontyLearnRunDocument, variables: { runId } })
			.pipe(map((result) => result.data.resumeMontyLearnRun));
	}
}
